// Odometer/distance unit handling (ADDVEH-ODO-002).
//
// The odometer is ALWAYS stored canonically in MILES (vehicles.total_distance_miles).
// The user's preferred display unit lives on their profile (users.distance_unit: 'mi'|'km').
// These helpers are the single source of truth for converting between the two so no
// component ever just relabels a number from "mi" to "km" without converting it.

// Maximum accepted odometer reading, in MILES (inclusive). Readings above this are
// rejected everywhere an odometer/mileage is entered (#412). The backend enforces the
// same limit on the canonical (miles) value.
export const MAX_ODOMETER_MILES = 1000000;
export const ODOMETER_TOO_HIGH_MESSAGE = 'Please enter a valid odometer reading (1,000,000 miles or less).';

export const MILES_TO_KM = 1.609344;        // multiply miles → kilometres
// Exact inverse of MILES_TO_KM (NOT the rounded 0.621371) so a km→mi→km round-trip is
// stable and the frontend matches the backend, which divides by 1.609344.
export const KM_TO_MILES = 1 / MILES_TO_KM; // multiply kilometres → miles

const normalizeUnit = (unit) => (String(unit || 'mi').trim().toLowerCase() === 'km' ? 'km' : 'mi');

// Parse a mileage-ish value (number or string like "45,000 mi") to a plain number.
export const parseMileageNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const digits = String(value).replace(/[^\d.]/g, '');
    if (!digits) return null;
    const parsed = Number(digits);
    return Number.isFinite(parsed) ? parsed : null;
};

// Convert a value the user entered in `unit` into MILES for storage.
export const toMiles = (value, unit = 'mi') => {
    const n = parseMileageNumber(value);
    if (n === null) return null;
    // Divide by MILES_TO_KM (same as the backend) rather than multiplying by a rounded
    // constant, so every path converts a km entry to exactly the same miles value.
    return normalizeUnit(unit) === 'km' ? Math.round(n / MILES_TO_KM) : Math.round(n);
};

// Convert a stored MILES value into the display `unit` (numeric, rounded).
export const milesToDisplay = (miles, unit = 'mi') => {
    const n = parseMileageNumber(miles);
    if (n === null) return null;
    return normalizeUnit(unit) === 'km' ? Math.round(n * MILES_TO_KM) : Math.round(n);
};

// Format a stored MILES value for display in the user's preferred unit, e.g. "72,000 km".
// Falls back to any non-numeric text as-is.
export const formatMileage = (miles, unit = 'mi') => {
    const display = milesToDisplay(miles, unit);
    if (display !== null) {
        return `${display.toLocaleString('en-US')} ${normalizeUnit(unit)}`;
    }
    const text = String(miles || '').trim();
    return text || '';
};

// Short unit label for inputs/adornments.
export const unitLabel = (unit = 'mi') => normalizeUnit(unit);
